import { useToast, HStack, Radio } from '@chakra-ui/react';
import { t, Trans } from '@lingui/macro';
import { Api } from 'api';
import { FormControlFormInput, FormControlFormRadioGroup, FormControlFormTextarea, getErrorMessage } from 'core';
import { useFormik } from 'formik';
import React from 'react';
import { useMutation } from 'react-query';
import { ExpertiseMasterNote, SaveExpertiseMasterNoteRequest } from 'types';
import { VehicleBrandSelect, VehicleModelSelect } from 'components';

import * as yup from 'yup';

export interface AdminSaveExpertiseMasterNoteFormProps {
  formId: string;
  focusedNote?: ExpertiseMasterNote;
  onSubmitStart?: () => void;
  onSubmitComplete?: (savedNote?: ExpertiseMasterNote) => void;
  noteType: number;
}

const validationSchema = yup.object({
  brandId: yup.mixed().required(t`Marka seçilmesi zorunludur`),
  modelId: yup.mixed().required(t`Model seçilmesi zorunludur`),
  note: yup.string().required(t`Not alanı boş bırakılamaz`),
});

export const AdminSaveExpertiseMasterNoteForm: React.FC<AdminSaveExpertiseMasterNoteFormProps> = ({
  formId,
  focusedNote,
  onSubmitStart,
  onSubmitComplete,
  noteType,
}) => {
  const toast = useToast();

  const saveMutation = useMutation<ExpertiseMasterNote, any, SaveExpertiseMasterNoteRequest>(
    async (data: SaveExpertiseMasterNoteRequest) => {
      const response = await Api.Expertise.saveMasterNote({
        id: focusedNote?.id,
        brandId: Number(data.brandId),
        modelId: Number(data.modelId),
        year: data.year ? Number(data.year) : undefined,
        note: data.note,
        noteType: Number(data.noteType),
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast({
          title: t`Not Başarılı Bir Şekilde Kayıt Edildi`,
          status: 'success',
          isClosable: true,
        });
        if (onSubmitComplete) {
          onSubmitComplete(data);
        }
      },
      onError: (err) => {
        toast({
          title: getErrorMessage(err, t`Not Kayıt Edilirken Hata Oluştu`),
          status: 'error',
          isClosable: true,
        });
        if (onSubmitComplete) {
          onSubmitComplete();
        }
      },
    },
  );

  const { handleSubmit, handleChange, values, errors, setFieldValue } = useFormik({
    enableReinitialize: true,
    initialValues: {
      brandId: focusedNote?.brandId || '',
      modelId: focusedNote?.modelId || '',
      year: focusedNote?.year || '',
      note: focusedNote?.note || '',
      noteType: (focusedNote?.noteType || noteType).toString(),
    },
    validationSchema,
    onSubmit: (data) => {
      if (onSubmitStart) {
        onSubmitStart();
      }
      saveMutation.mutateAsync({
        brandId: Number(data.brandId),
        modelId: Number(data.modelId),
        year: data.year ? Number(data.year) : undefined,
        note: data.note,
        noteType: Number(data.noteType),
      });
    },
  });

  return (
    <form onSubmit={handleSubmit} noValidate id={formId}>
      <VehicleBrandSelect
        name="brandId"
        value={values.brandId}
        onChange={(e) => {
          setFieldValue('brandId', e.target.value);
          setFieldValue('modelId', '');
        }}
        error={errors.brandId != null}
        helperText={errors.brandId}
        mb={4}
      />
      <VehicleModelSelect
        brandId={values.brandId}
        name="modelId"
        value={values.modelId}
        onChange={handleChange}
        error={errors.modelId != null}
        helperText={errors.modelId}
        mb={4}
      />
      <FormControlFormInput
        name="year"
        label={t`Yıl (Opsiyonel)`}
        placeholder={t`Model Yılı`}
        type="number"
        onChange={handleChange}
        value={values.year}
        error={errors.year != null}
        helperText={errors.year}
        mb={4}
      />
      <FormControlFormRadioGroup
        key="noteType"
        label={t`Not Türü`}
        onChange={(value) => {
          setFieldValue('noteType', value);
        }}
        value={values.noteType}
        mb={4}
      >
        <HStack spacing="24px">
          <Radio value="2">
            <Trans>Mekanik</Trans>
          </Radio>
          <Radio value="1">
            <Trans>Kaporta</Trans>
          </Radio>
        </HStack>
      </FormControlFormRadioGroup>
      <FormControlFormTextarea
        name="note"
        label={t`Not`}
        placeholder={t`Not metnini buraya yazınız...`}
        onChange={handleChange}
        value={values.note}
        error={errors.note != null}
        helperText={errors.note}
      />
    </form>
  );
};
